import React, { useCallback, useMemo, useState } from 'react';
import { Box, useColorModeValue } from '@chakra-ui/react';
import { useCampaignMappingsDataContext } from 'src/features/CampaignMapping/contexts/CampaignMappingsDataContext';
import { CampaignNameCell } from 'src/features/CampaignMapping/components/atoms/CampaignNameCell';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';
import { HeaderRow } from 'src/features/CampaignMapping/components/molecules/CampaignMappingHeaderRow';
import { useSyncedGridScroll } from 'src/features/CampaignMapping/hooks/useSyncedGridScroll';
import { CampaignMappingCell } from 'src/features/CampaignMapping/components/organisms/CampaignMappingCell';
import { CampaignMappingBody } from 'src/features/CampaignMapping/components/organisms/CampaignMappingBody';
import { CampaignMapping } from 'src/api/campaignMappings';
import {
  CAMPAIGN_NAME_COL_WIDTH,
  PLATFORM_COL_WIDTH,
  ROW_HEIGHT,
} from 'src/features/CampaignMapping/utils/constants';
import { useUpdateCampaignMappingNameMutation } from 'src/api/campaignMappings';
import { SortDirection } from 'src/features/CampaignMapping/types';
import { useAppContext } from 'src/contexts/AppContext';

const renderPlatformCell = (row: CampaignMapping, column: string) => {
  return <CampaignMappingCell row={row} column={column} />;
};

export const CampaignMappingView: React.FC = () => {
  const cardBg = useColorModeValue('white', 'background.dark.highest');
  const [searchValue, setSearchValue] = useState('');
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [editingCell, setEditingCell] = useState<{ rowIndex: number } | null>(
    null,
  );
  const [editValue, setEditValue] = useState('');
  const { currentCustomer } = useAppContext();
  const {
    campaignMappingsData,
    columns,
    isLoadingCampaignMappings,
    isErrorCampaignMappings,
  } = useCampaignMappingsDataContext();

  const { mutate: updateCampaignMapping } =
    useUpdateCampaignMappingNameMutation();

  const {
    leftGridRef,
    rightGridRef,
    headerScrollRef,
    syncFromTop,
    syncFromLeft,
    syncFromRight,
  } = useSyncedGridScroll();

  const filteredRows = useMemo(() => {
    let rows = [...campaignMappingsData];
    if (searchValue.trim()) {
      const lowerSearch = searchValue.toLowerCase();
      rows = rows.filter((row) =>
        row.campaign_name.toLowerCase().includes(lowerSearch),
      );
    }

    if (sortDirection) {
      rows.sort((a, b) => {
        const comparison = a.campaign_name.localeCompare(b.campaign_name);
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }
    return rows;
  }, [campaignMappingsData, searchValue, sortDirection]);

  const handleSortToggle = useCallback(() => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }, [setSortDirection]);

  const handleNameEdit = useCallback(
    (rowIndex: number) => {
      const row = filteredRows[rowIndex];
      if (!row) {
        setEditingCell(null);
        return;
      }
      const trimmedValue = editValue.trim();
      if (trimmedValue && trimmedValue !== row.campaign_name) {
        updateCampaignMapping({
          customer: currentCustomer?.customerName ?? 'missing_customer',
          campaignMappingId: row.campaign_id,
          name: trimmedValue,
        });
      }
      setEditingCell(null);
    },
    [
      filteredRows,
      editValue,
      currentCustomer?.customerName,
      updateCampaignMapping,
    ],
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent, rowIndex: number) => {
      if (e.key === 'Enter') {
        handleNameEdit(rowIndex);
      }
      if (e.key === 'Escape') {
        setEditingCell(null);
      }
    },
    [handleNameEdit],
  );

  const renderCampaignNameCell = useCallback(
    (row: CampaignMapping, rowIndex: number) => {
      const isEditing = editingCell?.rowIndex === rowIndex;
      return (
        <CampaignNameCell
          row={row}
          rowIndex={rowIndex}
          cardBg={cardBg}
          isEditing={isEditing}
          editValue={editValue}
          setEditValue={setEditValue}
          onEditCommit={handleNameEdit}
          onKeyPress={handleKeyPress}
          onStartEdit={(idx, value) => {
            setEditingCell({ rowIndex: idx });
            setEditValue(value);
          }}
        />
      );
    },
    [
      cardBg,
      editValue,
      editingCell,
      handleKeyPress,
      handleNameEdit,
      setEditValue,
    ],
  );

  const getRowHeight = useCallback(() => ROW_HEIGHT, []);

  if (isErrorCampaignMappings) {
    return <ErrorNotifBox title="Error fetching campaigns" />;
  }

  if (isLoadingCampaignMappings) {
    return <Box padding={4}>Loading campaign mappings...</Box>;
  }

  if (campaignMappingsData.length === 0 || columns.length === 0) {
    return (
      <ErrorNotifBox
        title="No campaign data found."
        description="Please connect channels or upload data."
      />
    );
  }

  return (
    <Box position="relative">
      <HeaderRow
        cardBg={cardBg}
        columns={columns}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSortToggle={handleSortToggle}
        sortOrder={sortDirection}
        headerScrollRef={headerScrollRef}
        onHeaderScroll={syncFromTop}
      />

      <CampaignMappingBody
        cardBg={cardBg}
        rows={filteredRows}
        columns={columns}
        renderCampaignCell={renderCampaignNameCell}
        renderPlatformCell={renderPlatformCell}
        leftGridRef={leftGridRef}
        rightGridRef={rightGridRef}
        syncFromLeft={syncFromLeft}
        syncFromRight={syncFromRight}
        rowHeight={getRowHeight}
        rowCount={filteredRows.length}
        campaignNameColWidth={CAMPAIGN_NAME_COL_WIDTH}
        platformColWidth={PLATFORM_COL_WIDTH}
      />
    </Box>
  );
};
